import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import classNames from 'classnames'
import { Spinner } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { type ConnectorResultError } from '../../../models/AccountOpening'
import { type StatusTone } from '../domain/connectorResult'
import styles from '../AccountOpeningWizard.module.scss'

const STATUS_TONE_CLASSES: Record<StatusTone, string> = {
  neutral: 'zocks-bg-color-gray-200 zocks-text-gray-700',
  progress: 'zocks-bg-color-primary-light zocks-text-primary',
  attention: 'zocks-bg-color-warning zocks-text-warning-dark',
  success: 'zocks-bg-color-success-background zocks-text-success',
}

interface StatusChipProps extends PropsWithChildren {
  tone: StatusTone
  size?: 'small' | 'medium'
  className?: string
}

export const StatusChip: FC<StatusChipProps> = ({ tone, size = 'small', className, children }) => (
  <span
    className={classNames(
      size === 'medium' ? 'paragraph-medium' : 'paragraph-small',
      'medium rounded-1 px-2 py-1 d-inline-block',
      STATUS_TONE_CLASSES[tone],
      className,
    )}
  >
    {children}
  </span>
)

interface HintTextProps extends PropsWithChildren {
  className?: string
}

export const HintText: FC<HintTextProps> = ({ className, children }) => (
  <p className={classNames('paragraph-small zocks-text-gray-600 mb-0', className)}>{children}</p>
)

export const ErrorText: FC<PropsWithChildren> = ({ children }) => <p className='paragraph-small zocks-text-danger-200 mb-0'>{children}</p>

export const WarningText: FC<PropsWithChildren> = ({ children }) => (
  <p className='paragraph-small zocks-text-warning-text d-flex align-items-center zocks-gap-0.25 mb-0'>
    <i className='bi bi-exclamation-triangle' />
    {children}
  </p>
)

export const SpinnerLine: FC<HintTextProps> = ({ className, children }) => (
  <div className={classNames('d-flex align-items-center zocks-gap-0.5', className)}>
    <Spinner variant='primary' size='sm' />
    <span className='paragraph-small zocks-text-gray-600'>{children}</span>
  </div>
)

interface StepActionBarProps extends PropsWithChildren {
  message: ReactNode
}

export const StepActionBar: FC<StepActionBarProps> = ({ message, children }) => (
  <div className='border-top pt-3 d-flex align-items-center justify-content-end zocks-gap-1'>
    <span className='paragraph-medium'>{message}</span>
    {children}
  </div>
)

interface StepConfirmBarProps {
  message: ReactNode
  label: string
  disabled: boolean
  loading?: boolean
  discard?: ReactNode
  prefill?: ReactNode
  onConfirm: () => void
}

export const StepConfirmBar: FC<StepConfirmBarProps> = ({ message, label, disabled, loading = false, discard, prefill, onConfirm }) => (
  <StepActionBar message={message}>
    {discard}
    {prefill}
    <Button variant='primary' disabled={disabled} loading={loading} onClick={onConfirm}>
      {label}
    </Button>
  </StepActionBar>
)

interface StepFooterProps {
  confirm: ReactNode
  prefill: ReactNode
  error?: string | null
}

export const StepFooter: FC<StepFooterProps> = ({ confirm, prefill, error }) => {
  const actions = confirm ?? (prefill ? <div className='d-flex justify-content-end'>{prefill}</div> : null)
  if (!actions && !error) {
    return null
  }
  return (
    <>
      {error ? <ErrorText>{error}</ErrorText> : null}
      {actions}
    </>
  )
}

interface IconActionButtonProps {
  icon: string
  label: string
  loading: boolean
  onClick: () => void
  loadingLabel?: string
  className?: string
}

export const IconActionButton: FC<IconActionButtonProps> = ({ icon, label, loading, loadingLabel, onClick, className }) => (
  <Button variant='secondary' className={classNames('d-flex align-items-center zocks-gap-0.5', className)} loading={loading} onClick={onClick}>
    {loading ? null : <i className={classNames('bi', icon)} />}
    {loading && loadingLabel ? loadingLabel : label}
  </Button>
)

interface PrefillButtonProps {
  label: string
  loading: boolean
  onClick: () => void
}

export const PrefillButton: FC<PrefillButtonProps> = ({ label, loading, onClick }) => (
  <IconActionButton icon='bi-arrow-clockwise' label={label} loading={loading} onClick={onClick} className='flex-shrink-0' />
)

export const SuccessLine: FC<PropsWithChildren> = ({ children }) => (
  <div className='d-flex align-items-center zocks-gap-0.5'>
    <span className={classNames('bg-success rounded-circle d-flex align-items-center justify-content-center flex-shrink-0', styles.statusDot)}>
      <i className='bi bi-check-lg zocks-text-white' />
    </span>
    <span className='paragraph-medium medium zocks-text-success'>{children}</span>
  </div>
)

interface ConnectorIssuesProps {
  heading: ReactNode
  errors: ConnectorResultError[]
}

export const ConnectorIssues: FC<ConnectorIssuesProps> = ({ heading, errors }) =>
  errors && errors.length > 0 ? (
    <div className='d-flex flex-column zocks-gap-0.25 p-3 border zocks-border-color-gray-300 rounded-3'>
      <span className='paragraph-medium medium'>{heading}</span>
      {errors.map((error, index) => (
        <span key={`${error.field ?? 'issue'}-${index}`} className='paragraph-small zocks-text-danger-200'>
          {error.message}
          {error.field ? <span className='zocks-text-gray-600'> ({error.field})</span> : null}
        </span>
      ))}
    </div>
  ) : null
